import { useEffect, Dispatch, SetStateAction } from "react";
import { useRouter } from "next/router";

import { EditorContent as ReadOnlyEditorContent } from "@tiptap/react";
import { listenModeHighlightDecoratorPluginKey } from "@/view/TiptapArticleBody/extensions/ListenModeHighlightDecorator";
import ActivityChoiceIndicator from "@/components/ActivityChoiceIndicator";
import ALPHABET from "@/view/constants/alphabet";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import SelectedStateIndicator from "@/components/SelectedStateIndicator";
import ReadAloudButton, {
  ButtonTypes,
} from "@/view/ReadAloudButton/ReadAloudButton";
import { ReadAloudContentModes } from "@/view/ReadAloudControls/useReadAloudMode/constants";
import { INode } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";
import useMultipleChoiceEditor from "@/view/QuizQuestion/useMultipleChoiceEditor/";
import { Letter } from "@/view/QuizQuestion/constants";
import {
  choiceLetterCss,
  choiceTextCss,
  inputCss,
  multipleChoiceCss,
  multipleChoiceContainerCss,
  interactiveMultipleChoiceLabelCss,
  containerCss,
} from "./InteractiveMultipleChoice.style";

interface MultipleChoiceProps {
  choiceIndex: number;
  choiceText: string | null;
  choiceType: string;
  groupName: string;
  handleSelect: (a: string) => void;
  currentReadAloudChoice: Letter | null;
  isReadAloudButtonActive?: boolean;
  isSelected: boolean;
  isSubmitted?: boolean;
  optionId: string;
  rawChoiceText: INode | null;
  selectedChoiceContentId?: string;
  setCurrentReadAloudChoice: Dispatch<SetStateAction<Letter | null>>;
  userSelectedCorrectChoice?: boolean;
  textId?: string;
}

export const InteractiveMultipleChoice = ({
  choiceIndex,
  choiceText,
  currentReadAloudChoice,
  groupName,
  handleSelect,
  isSelected,
  isReadAloudButtonActive = false,
  isSubmitted = false,
  optionId,
  choiceType,
  userSelectedCorrectChoice = false,
  rawChoiceText,
  selectedChoiceContentId = "",
  setCurrentReadAloudChoice,
}: MultipleChoiceProps) => {
  const router = useRouter();
  const {
    query: { levelId },
  } = router;

  const {
    currentSpeech,
    isReadAloudModeActive,
    readAloudContentMode,
    currentMark,
  } = useReadAloudMode();

  const READ_ALOUD_CONTENT_MODE = ReadAloudContentModes.ANSWER;

  const editor = useMultipleChoiceEditor({
    content: rawChoiceText,
    dataTestId: "TipTapQuizQuestionChoice-Editor-interactive",
    levelId,
  });

  // Dispatch the transaction and update the editor state (apply highlighting to selected words)
  useEffect(() => {
    if (editor) {
      const metaData =
        !isReadAloudModeActive ||
        readAloudContentMode !== READ_ALOUD_CONTENT_MODE ||
        currentReadAloudChoice !== ALPHABET.toUpperCase()[choiceIndex]
          ? {}
          : { ...currentMark };
      const transaction = editor.state.tr.setMeta(
        listenModeHighlightDecoratorPluginKey,
        metaData,
      );
      editor.view.dispatch(transaction);
    }
  }, [
    JSON.stringify(currentMark),
    isReadAloudModeActive,
    JSON.stringify(rawChoiceText),
  ]);

  // Pause previous quiz answer if we're on a new one
  useEffect(() => {
    if (readAloudContentMode === READ_ALOUD_CONTENT_MODE && currentSpeech) {
      currentSpeech.pauseAudio();
    }
  }, [choiceIndex]);

  if (!editor || !rawChoiceText) return null;

  const alphabeticAnswer = (ALPHABET.toUpperCase()[choiceIndex] ??
    "") as Letter;
  const inputId = `${choiceType}-${choiceIndex}`;

  // Screen readers were not reading the option text correctly, so instead we're using an `aria-label` on the input and hiding the option text with `aria-hidden`.
  const userHasSelectedQuestion = () => selectedChoiceContentId === optionId;

  const renderLabel = () => {
    if (
      !isSubmitted ||
      (!userSelectedCorrectChoice && !userHasSelectedQuestion())
    )
      return null;

    const labelProps = {
      labelColor: !userSelectedCorrectChoice ? "primary" : "success",
      status: !userSelectedCorrectChoice ? "primary" : "correct",
      text: !userSelectedCorrectChoice ? "Your answer" : "Correct answer",
    };

    return (
      <ActivityChoiceIndicator
        __cssFor={interactiveMultipleChoiceLabelCss}
        labelColor={labelProps.labelColor}
        status={labelProps.status}
        text={labelProps.text}
      />
    );
  };

  const renderProperAriaLabel = () => {
    const baseText = `${alphabeticAnswer + " " + choiceText}`;
    if (
      !isSubmitted ||
      (!userSelectedCorrectChoice && !userHasSelectedQuestion())
    )
      return baseText;

    return !userSelectedCorrectChoice
      ? `Your answer: ${baseText}`
      : `Correct answer: ${baseText}`;
  };
  return (
    <div
      css={containerCss}
      data-testid={`quizQuestionInteractiveuseMultipleChoiceEditorWrapper-${alphabeticAnswer}`}
    >
      <label
        htmlFor={inputId}
        css={multipleChoiceContainerCss(
          {
            userSelectedCorrectChoice,
            isSubmitted,
            userHasSelectedQuestion: userHasSelectedQuestion(),
          },
          isReadAloudButtonActive,
        )}
      >
        <input
          aria-label={renderProperAriaLabel()}
          aria-checked={isSelected}
          css={inputCss}
          id={inputId}
          name={groupName}
          onChange={() => handleSelect(optionId)}
          type="radio"
          value={choiceIndex}
          checked={isSelected}
          disabled={isSubmitted}
        />
        <div aria-hidden="true" css={[multipleChoiceCss]}>
          {renderLabel()}
          <SelectedStateIndicator
            shouldDisplay={isSubmitted ? false : isSelected}
          />
          <span css={choiceLetterCss}>{alphabeticAnswer}</span>
          <span css={choiceTextCss}>
            <ReadOnlyEditorContent
              editor={editor}
              data-testid={`readOnlyEditorMultipleChoiceContent-${alphabeticAnswer}`}
            />
          </span>
        </div>
      </label>
      {isReadAloudButtonActive && (
        <ReadAloudButton
          buttonContentMode={READ_ALOUD_CONTENT_MODE}
          position={alphabeticAnswer}
          setCurrentReadAloudChoice={() =>
            setCurrentReadAloudChoice(alphabeticAnswer)
          }
          text={rawChoiceText}
          type={ButtonTypes.answer}
        />
      )}
    </div>
  );
};

export default InteractiveMultipleChoice;
